/**
 * "Today" means today in New England, not in UTC and not on the reader's own
 * clock.
 *
 * Event dates are civil dates — "2026-09-19", what an organizer means by
 * Saturday — so they have to be compared against a civil date in the zone they
 * were written in. Reading them in UTC was wrong for four hours every evening:
 * after 8pm Eastern the UTC date has already rolled over, so tomorrow's events
 * were being headed "TODAY" while tonight's had quietly become yesterday.
 *
 * Eastern rather than the device's own timezone, because the event happens in
 * Hartford whether or not the person reading is: a dance tonight is tonight
 * even when it is read from California.
 */
const EASTERN_ISO = new Intl.DateTimeFormat("en-CA", {
  timeZone: "America/New_York",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

/** Today's civil date in Eastern time, as "YYYY-MM-DD". */
export function getTodayISO(): string {
  return EASTERN_ISO.format(new Date());
}

/** `days` after today, Eastern. */
function daysFromToday(days: number): string {
  const d = new Date(`${getTodayISO()}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().split("T")[0]!;
}

export function isToday(dateString: string): boolean {
  return dateString === getTodayISO();
}

export function isTomorrow(dateString: string): boolean {
  return dateString === daysFromToday(1);
}

export function formatDateHeader(dateString: string): string {
  if (isToday(dateString)) {
    const date = new Date(dateString + "T00:00:00");
    return `TODAY - ${date.toLocaleDateString("en-US", {
      weekday: "long",
      month: "short",
      day: "numeric",
    })}`;
  }

  if (isTomorrow(dateString)) {
    const date = new Date(dateString + "T00:00:00");
    return `TOMORROW - ${date.toLocaleDateString("en-US", {
      weekday: "long",
      month: "short",
      day: "numeric",
    })}`;
  }

  const date = new Date(dateString + "T00:00:00");
  return date
    .toLocaleDateString("en-US", {
      weekday: "long",
      month: "short",
      day: "numeric",
    })
    .toUpperCase();
}

/**
 * "Sat, Sep 19" — the compact form used wherever an event is listed outside
 * the main date-grouped list.
 *
 * Parsed and formatted as UTC so the civil date cannot be shifted a day
 * backwards by the reader's own timezone; the string is a calendar date, not
 * an instant. The year is shown only when it is not the current one, since a
 * venue's listing can run two years out and "Sat, Jan 3" alone would be a
 * guess.
 */
export function formatShortDate(iso: string): string {
  const date = new Date(`${iso}T00:00:00Z`);
  const showYear = iso.slice(0, 4) !== getTodayISO().slice(0, 4);
  return date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    ...(showYear ? { year: "numeric" } : {}),
    timeZone: "UTC",
  });
}

export function formatTime(time24: string): string {
  const parts = time24.split(":");
  const hourStr = parts[0] ?? "00";
  const minute = parts[1] ?? "00";
  const hour = parseInt(hourStr, 10);
  const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  const ampm = hour < 12 ? "AM" : "PM";
  return `${hour12}:${minute} ${ampm}`;
}

export function formatTimeRange(startTime: string, endTime: string | undefined): string {
  const start = formatTime(startTime);
  if (!endTime) return start;
  const end = formatTime(endTime);
  return `${start} - ${end}`;
}



// Calendar utilities

export function isSameDay(date1: Date, date2: Date): boolean {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
}

export function toISODateString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function formatMonthYear(date: Date): string {
  return date.toLocaleDateString("en-US", { month: "long", year: "numeric" });
}

export interface CalendarDay {
  date: Date;
  dateString: string;
  isCurrentMonth: boolean;
  isToday: boolean;
}

export function getCalendarDays(month: Date): CalendarDay[] {
  const year = month.getFullYear();
  const monthIndex = month.getMonth();

  // First day of the month
  const firstDay = new Date(year, monthIndex, 1);
  // Last day of the month
  const lastDay = new Date(year, monthIndex + 1, 0);

  // How many days of the previous month lead the grid, counting from Monday.
  const startDayOfWeek = mondayOffset(firstDay);
  // Total days in month
  const daysInMonth = lastDay.getDate();

  const days: CalendarDay[] = [];

  // Add days from previous month to fill the first week
  const prevMonthLastDay = new Date(year, monthIndex, 0).getDate();
  for (let i = startDayOfWeek - 1; i >= 0; i--) {
    days.push(makeCalendarDay(new Date(year, monthIndex - 1, prevMonthLastDay - i), false));
  }

  // Add days of current month
  for (let day = 1; day <= daysInMonth; day++) {
    days.push(makeCalendarDay(new Date(year, monthIndex, day), true));
  }

  // Add days from next month to complete the grid (6 rows = 42 cells)
  const remainingDays = 42 - days.length;
  for (let day = 1; day <= remainingDays; day++) {
    days.push(makeCalendarDay(new Date(year, monthIndex + 1, day), false));
  }

  return days;
}

/**
 * A day column in the week and weekend grids, and a cell in the month grid.
 *
 * `isCurrentMonth` is only meaningful in the month grid, where the leading and
 * trailing days belong to the neighbouring months; the week and weekend grids
 * set it against their own anchor month so a week straddling the turn of a
 * month still dims nothing — every column there is a day the reader asked for.
 */
function makeCalendarDay(date: Date, isCurrentMonth: boolean): CalendarDay {
  const dateString = toISODateString(date);
  return { date, dateString, isCurrentMonth, isToday: dateString === getTodayISO() };
}

/** `n` days after `date`, as a new local Date. */
function addDays(date: Date, n: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + n);
}

/**
 * The Monday-to-Sunday week containing `anchor`.
 *
 * Monday rather than Sunday so the week ends on its weekend: Friday, Saturday
 * and Sunday land together as the last three columns instead of Sunday being
 * marooned at the far left, a week away from the two nights it belongs with.
 */
export function getWeekDays(anchor: Date): CalendarDay[] {
  const monday = addDays(anchor, -mondayOffset(anchor));
  return Array.from({ length: 7 }, (_, i) => makeCalendarDay(addDays(monday, i), true));
}

/** How many days `date` sits past the Monday of its week. */
function mondayOffset(date: Date): number {
  return (date.getDay() + 6) % 7;
}

/**
 * The Friday, Saturday and Sunday of `anchor`'s weekend.
 *
 * A weekend is read as the one still ahead of you: Monday through Thursday
 * look forward to the coming Friday, while Friday, Saturday and Sunday stay on
 * the weekend already underway rather than skipping to the next one — on a
 * Saturday afternoon "this weekend" is today, not six days out.
 */
export function getWeekendDays(anchor: Date): CalendarDay[] {
  const dow = anchor.getDay();
  const offsetToFriday = dow === 0 ? -2 : dow === 6 ? -1 : 5 - dow;
  const friday = addDays(anchor, offsetToFriday);
  return Array.from({ length: 3 }, (_, i) => makeCalendarDay(addDays(friday, i), true));
}

/**
 * "Sep 14 – 20, 2026" — the span a set of day columns covers, collapsing the
 * parts the two ends share so the header reads as one range and not as two
 * dates that happen to sit together.
 */
export function formatDayRange(days: CalendarDay[]): string {
  const first = days[0]?.date;
  const last = days[days.length - 1]?.date;
  if (!first || !last) return "";

  const year = last.getFullYear();
  const sameMonth = first.getMonth() === last.getMonth() && first.getFullYear() === year;
  const startMonth = first.toLocaleDateString("en-US", { month: "short" });
  const endMonth = last.toLocaleDateString("en-US", { month: "short" });

  return sameMonth
    ? `${startMonth} ${first.getDate()} – ${last.getDate()}, ${year}`
    : `${startMonth} ${first.getDate()} – ${endMonth} ${last.getDate()}, ${year}`;
}

/** Minutes since midnight for a "HH:MM" time. */
export function timeToMinutes(time24: string): number {
  const [hourStr, minuteStr] = time24.split(":");
  const hour = parseInt(hourStr ?? "0", 10);
  const minute = parseInt(minuteStr ?? "0", 10);
  return (Number.isNaN(hour) ? 0 : hour) * 60 + (Number.isNaN(minute) ? 0 : minute);
}

/**
 * "8p", "7:30p" — a start time for somewhere there is barely room for one.
 *
 * The month grid gives a chip about a dozen characters; spending four of them
 * on "8 PM" costs more of the title than the extra letters are worth, and a
 * lone lowercase a/p is unambiguous once the reader has seen two of them.
 */
export function formatTimeCompact(time24: string): string {
  const minutes = timeToMinutes(time24);
  const hour = Math.floor(minutes / 60) % 24;
  const minute = minutes % 60;
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  const suffix = hour < 12 ? "a" : "p";
  return minute === 0 ? `${hour12}${suffix}` : `${hour12}:${String(minute).padStart(2, "0")}${suffix}`;
}
